<template>
    <div
        class="relative"
        :class="[wrapperClasses, {'items-center grid grid-cols-[1fr_2fr]': horizontal}]"
    >
        <Label
            v-if="label"
            :label="label + (isRequired ? ' *' : '')"
            :for="id"
            :class="{'pr-8 text-right': horizontal }"
        />
        <textarea
            :id="id"
            v-model="_value"
            :name="name || id"
            class="block w-full text-black bg-white border disabled:bg-light-gray
                    p-2 text-lg disabled:cursor-not-allowed min-h-[6rem] outline-none"
            :class="invalid || validationMessage
                ? 'border-red'
                : 'border-medium-gray'"
            v-bind="$attrs"
            @blur="onBlur"
            @input="onInput"
        ></textarea>
        <InputErrorMessage
            v-if="validationMessage"
            :message="validationMessage"
        />
    </div>
</template>

<script setup lang="ts">
import { useField, type RuleExpression } from 'vee-validate';
import { debounce as debounceFunction } from '~/helpers';
import useValidation from '~/composables/useValidation';

interface Props {
    id: string;
    name?: string;
    label?: string;
    value?: string;
    modelValue?: string | null;
    rules?: RuleExpression<string>;
    horizontal?: boolean;
    wrapperClasses?: string;
    debounce?: number;
    invalid?: boolean;
    validateOnBlur?: boolean;
}

interface Events {
    (event: 'update:modelValue', value: Props['modelValue']): void;
    (event: 'update:invalid', value: false): void;
    (event: 'input', value: Event): void;
}

const props = withDefaults(defineProps<Props>(), {
    name: undefined,
    label: undefined,
    value: undefined,
    modelValue: undefined,
    rules: undefined,
    horizontal: false,
    wrapperClasses: undefined,
    debounce: 0,
    validateOnUpdate: false,
    validateOnBlur: true,
    invalid: false,
});

const emit = defineEmits<Events>();

const {
    errorMessage, setValue, validate, setTouched, resetField,
} = useField<string>(props.name || props.id, props.rules, {
    validateOnValueUpdate: props.validateOnUpdate,
    label: props.label,
    type: 'default',
    initialValue: String(props.modelValue ?? ''),
});

const internalValue = ref(props.value ?? '');
const _value = computed({
    get: () => props.modelValue ?? internalValue.value,
    set: debounceFunction(newValue => {
        clearServerValidationError();
        setValue(newValue ?? '', props.validateOnUpdate);
        internalValue.value = newValue ?? '';
        emit('update:modelValue', newValue);
    }, props.debounce),
});

const { isRequired, validationMessage, clearServerValidationError } = useValidation(props, { errorMessage });

const onInput = debounceFunction((event: Event) => {
    setTouched(props.validateOnUpdate);
    emit('input', event);
}, props.debounce);

function onBlur() {
    if (!props.validateOnBlur) {
        return;
    }

    if (!isRequired.value && !_value.value) {
        resetField();
    } else {
        setTouched(true);
        validate();
    }
}

watch(() => props.value, newValue => {
    _value.value = newValue ?? '';
});
</script>

<script lang="ts">
/* eslint-disable unicorn/prevent-abbreviations */
export default {
    inheritAttrs: false,
    customOptions: {},
};
</script>
